import { useFileMessages } from '../../hooks/store/useMessageHooks';
import { useFileMessageLoader } from '../../hooks/firebase/useFileMessageLoader';
import React, { useCallback, useRef, useState } from 'react';
import {
  useIntersectionObserver,
  useLocale,
  LoadingIndicator,
} from '@xolv-mybrightlink/common';
import { format } from 'date-fns';
import { Message } from '../../type';
import {
  Box,
  ImageList,
  List,
  ListItem,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import { FileUploadType } from '../../store/types';
import { ColumnQuery, useColumnCount } from './useColumnCount';

type GalleryViewProps = {
  channelGuid: string;
  fileUploadType: FileUploadType;
  columnQuery: ColumnQuery;
  itemBuilder: (message: Message) => React.ReactNode;
  gap?: number;
  timeStyle?: SxProps<Theme>;
};

export const GalleryView = (props: GalleryViewProps) => {
  const { channelGuid, fileUploadType } = props;
  const locale = useLocale();
  const messages = useFileMessages(channelGuid, fileUploadType);
  const loadMessages = useFileMessageLoader(channelGuid, fileUploadType);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const loadMoreMessages = useCallback(async () => {
    if (hasMore) {
      setIsLoading(true);
      const hasMore = await loadMessages();
      setHasMore(hasMore);
      setIsLoading(false);
    }
  }, [hasMore, loadMessages]);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  useIntersectionObserver(loadMoreRef, loadMoreMessages);

  const cols = useColumnCount(props.columnQuery);

  if (!messages) {
    return <LoadingIndicator ref={loadMoreRef} fullHeight />;
  }

  const groupedByMonth = messages.reduce((result, message) => {
    const month = format(message.createdDate.toDate(), 'MMMM yyyy', { locale });
    const messages = result[month] ?? [];
    messages.push(message);
    result[month] = messages;
    return result;
  }, {} as { [month: string]: Message[] });

  return (
    <List
      className='hide-scrollbar'
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        height: '100%',
        overflow: 'auto',
      }}
    >
      {Object.entries(groupedByMonth).map((entry) => {
        const [month, messagesInMonth] = entry;
        return (
          <ListItem
            key={month}
            sx={{ display: 'flex', flexDirection: 'column' }}
          >
            <Typography
              sx={{
                alignSelf: 'flex-start',
                fontSize: 14,
                pb: 2.5,
                ...props.timeStyle,
              }}
            >
              {month}
            </Typography>
            <ImageList cols={cols} gap={props.gap} sx={{ width: '100%' }}>
              {messagesInMonth.map(props.itemBuilder)}
            </ImageList>
          </ListItem>
        );
      })}
      <Box ref={loadMoreRef} />
      {isLoading && (
        <Box sx={{ p: 4 }}>
          <LoadingIndicator />
        </Box>
      )}
    </List>
  );
};
